import React, { useState, useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { orderBy } from 'lodash'
import {
  ArrowBack,
  AccessTime,
  Delete,
  Add,
  CalendarToday,
  AutoAwesome,
} from '@mui/icons-material'

import DeleteIcon from 'assets/icons/Delete.svg'
import CopiarIcon from 'assets/icons/Copiar.svg'

import { Header, Card, Footer, Loading } from 'components'
import * as Global from 'global/styles.global'

import * as S from './styles'

import smartAgendaAnalytics from 'services/analytics/SmartAgenda/smartAgendaAnalytics'

import {
  createWeeklySchedule,
  buildWeeklySchedulePayload,
  fetchWeeklySchedules,
  deleteWeeklySchedule,
  formatDateFromAPI,
  formatDayIntervals,
  SavedWeeklySchedule,
} from './services'

interface TimeSlot {
  id: string
  from: string
  to: string
}

interface DaySchedule {
  [key: string]: TimeSlot[]
}

interface Exception {
  id: string
  reason: string
  startDate: string
  startTime: string
  endTime: string
  error?: string
}

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Segunda-feira' },
  { key: 'tuesday', label: 'Terça-feira' },
  { key: 'wednesday', label: 'Quarta-feira' },
  { key: 'thursday', label: 'Quinta-feira' },
  { key: 'friday', label: 'Sexta-feira' },
  { key: 'saturday', label: 'Sábado' },
  { key: 'sunday', label: 'Domingo' },
]

const applyDateMask = (value: string): string => {
  const numbers = value.replace(/\D/g, '')
  let masked = ''

  if (numbers.length > 0) {
    masked = numbers.substring(0, 2)
  }
  if (numbers.length > 2) {
    masked += '/' + numbers.substring(2, 4)
  }
  if (numbers.length > 4) {
    masked += '/' + numbers.substring(4, 8)
  }

  return masked
}

const parseDate = (dateStr: string): Date | null => {
  if (dateStr.length !== 10) return null
  const [day, month, year] = dateStr.split('/')
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
  if (isNaN(date.getTime())) return null
  return date
}

const isValidDate = (dateStr: string): boolean => {
  if (dateStr.length !== 10) return false
  const date = parseDate(dateStr)
  if (!date) return false

  const [day, month, year] = dateStr.split('/').map(Number)
  return (
    date.getDate() === day &&
    date.getMonth() === month - 1 &&
    date.getFullYear() === year
  )
}

const addMonths = (date: Date, months: number): Date => {
  const result = new Date(date)
  result.setMonth(result.getMonth() + months)
  return result
}

const DAY_KEY_TO_NUM: Record<string, number> = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 7,
}

const MIN_SCHEDULE_TIME = '08:00'

const parseTimeToMinutes = (time: string): number | null => {
  const match = time.match(/^(\d{2}):(\d{2})$/)
  if (!match) return null

  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 23 || minutes > 59) return null

  return hours * 60 + minutes
}

const isTimeBelowMinimum = (time: string): boolean => {
  const minutes = parseTimeToMinutes(time)
  const minMinutes = parseTimeToMinutes(MIN_SCHEDULE_TIME)
  if (minutes === null || minMinutes === null) return false
  return minutes < minMinutes
}

const isMeaningfulTimeSlot = (slot: TimeSlot): boolean => {
  if (!slot.from || !slot.to) return false
  return slot.from < slot.to
}

const computePatternMetrics = (daySchedules: DaySchedule) => {
  const daysWithRanges: number[] = []
  let time_ranges_count = 0

  for (const { key } of DAYS_OF_WEEK) {
    const slots = daySchedules[key] || []
    const meaningful = slots.filter(isMeaningfulTimeSlot)
    if (meaningful.length > 0) {
      daysWithRanges.push(DAY_KEY_TO_NUM[key])
      time_ranges_count += meaningful.length
    }
  }

  return {
    patterns_count: daysWithRanges.length,
    days_of_week: [...daysWithRanges].sort((a, b) => a - b).join(','),
    time_ranges_count,
  }
}

const computeDateRangeWeeks = (start: string, end: string): number => {
  const a = parseDate(start)
  const b = parseDate(end)
  if (!a || !b) return 0
  const diffMs = b.getTime() - a.getTime()
  const weeks = Math.ceil(diffMs / (7 * 24 * 60 * 60 * 1000))
  return Math.max(1, weeks)
}

const isStartDateTodayOrFuture = (start: string): boolean => {
  const d = parseDate(start)
  if (!d) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const copy = new Date(d)
  copy.setHours(0, 0, 0, 0)
  return copy >= today
}

/** Mesmas regras que validateDates, mais início >= hoje (período futuro). Sem setState. */
const isBulkDateRangeValid = (start: string, end: string): boolean => {
  if (!isValidDate(start) || !isValidDate(end)) return false
  const startDateParsed = parseDate(start)
  const endDateParsed = parseDate(end)
  if (!startDateParsed || !endDateParsed) return false
  if (endDateParsed < startDateParsed) return false
  const maxEndDate = addMonths(startDateParsed, 3)
  if (endDateParsed > maxEndDate) return false
  return isStartDateTodayOrFuture(start)
}

interface Specialty {
  id: string
  name: string
  attendance_time: number
  slug?: string
  status?: string
}

const OpenSchedule: React.FC = () => {
  const history = useHistory()

  const [loading, setLoading] = useState(true)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [dateError, setDateError] = useState('')
  const [scheduleError, setScheduleError] = useState('')
  const [daySchedules, setDaySchedules] = useState<DaySchedule>({
    monday: [{ id: '1', from: '00:00', to: '00:00' }],
    tuesday: [{ id: '1', from: '00:00', to: '00:00' }],
    wednesday: [{ id: '1', from: '00:00', to: '00:00' }],
    thursday: [{ id: '1', from: '00:00', to: '00:00' }],
    friday: [{ id: '1', from: '00:00', to: '00:00' }],
    saturday: [{ id: '1', from: '00:00', to: '00:00' }],
    sunday: [],
  })
  const [exceptions, setExceptions] = useState<Exception[]>([])

  const [userId, setUserId] = useState('')
  const [specialties, setSpecialties] = useState<Specialty[]>([])
  const [selectedSpecialty, setSelectedSpecialty] = useState<Specialty | null>(
    null
  )
  const [countries, setCountries] = useState<string[]>([])

  const [savedSchedules, setSavedSchedules] = useState<SavedWeeklySchedule[]>(
    []
  )

  const [showNewPeriodForm, setShowNewPeriodForm] = useState(false)

  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean
    scheduleId: string
    startDate: string
    endDate: string
  }>({
    isOpen: false,
    scheduleId: '',
    startDate: '',
    endDate: '',
  })

  const patternStartSentRef = useRef(false)
  const patternDefinedSentRef = useRef(false)
  const lastDateRangeKeyRef = useRef('')
  const bulkPreviewSentRef = useRef(false)

  const loadSavedSchedules = (id: string, showFormIfEmpty = false) => {
    fetchWeeklySchedules(id, setSavedSchedules, setLoading, (schedules) => {
      if (showFormIfEmpty && schedules.length === 0) {
        setShowNewPeriodForm(true)
      }
    })
  }

  const getSpecialtyName = (specialtyId: string): string => {
    const specialty = specialties.find((s) => s.id === specialtyId)
    return specialty?.name || 'Especialidade'
  }

  const openDeleteModal = (schedule: SavedWeeklySchedule) => {
    setDeleteModal({
      isOpen: true,
      scheduleId: schedule._id,
      startDate: formatDateFromAPI(schedule.start_date),
      endDate: formatDateFromAPI(schedule.end_date),
    })
  }

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      scheduleId: '',
      startDate: '',
      endDate: '',
    })
  }

  const confirmDeleteSchedule = async () => {
    const success = await deleteWeeklySchedule(
      deleteModal.scheduleId,
      setLoading
    )
    if (success) {
      loadSavedSchedules(userId)
    }
    closeDeleteModal()
  }

  const handleCopySchedule = (schedule: SavedWeeklySchedule) => {
    const newDaySchedules: DaySchedule = {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: [],
    }

    const dayKeyMap: { [key: number]: string } = {
      1: 'monday',
      2: 'tuesday',
      3: 'wednesday',
      4: 'thursday',
      5: 'friday',
      6: 'saturday',
      7: 'sunday',
    }

    schedule.weekly_intervals.forEach((wi) => {
      const dayKey = dayKeyMap[wi.day_of_week]
      if (dayKey) {
        newDaySchedules[dayKey] = wi.intervals.map((interval, index) => ({
          id: `${Date.now()}-${index}`,
          from: interval.start,
          to: interval.end,
        }))
      }
    })

    setDaySchedules(newDaySchedules)

    const specialty = specialties.find((s) => s.id === schedule.specialty_id)
    if (specialty) {
      setSelectedSpecialty(specialty)
    }

    setExceptions([])
    setScheduleError('')

    setShowNewPeriodForm(true)

    setTimeout(() => {
      window.scrollTo({ top: 600, behavior: 'smooth' })
    }, 100)
  }

  useEffect(() => {
    const userDataStorage = window.localStorage.getItem('@StarBem:user')
    if (userDataStorage) {
      const userData = JSON.parse(userDataStorage)
      setUserId(userData.id)

      const activeSpecialties = orderBy(
        userData.specialties?.filter((item: any) => item.status === 'active') ||
          [],
        ['name'],
        ['asc']
      )
      setSpecialties(activeSpecialties)

      if (activeSpecialties.length > 0) {
        setSelectedSpecialty(activeSpecialties[0])
      }

      setCountries(userData.countries || [])

      if (userData.id) {
        loadSavedSchedules(userData.id, true)
      }
    }
  }, [])

  useEffect(() => {
    if (!showNewPeriodForm) {
      patternStartSentRef.current = false
      patternDefinedSentRef.current = false
      lastDateRangeKeyRef.current = ''
      bulkPreviewSentRef.current = false
    }
  }, [showNewPeriodForm])

  useEffect(() => {
    if (!showNewPeriodForm || !userId) return
    if (patternStartSentRef.current) return
    patternStartSentRef.current = true
    smartAgendaAnalytics.trackPatternStart({ professional_id: userId })
  }, [showNewPeriodForm, userId])

  useEffect(() => {
    if (!showNewPeriodForm || !userId) return
    const m = computePatternMetrics(daySchedules)
    if (m.patterns_count === 0) {
      patternDefinedSentRef.current = false
      return
    }
    const t = window.setTimeout(() => {
      if (patternDefinedSentRef.current) return
      patternDefinedSentRef.current = true
      smartAgendaAnalytics.trackPatternDefined({
        professional_id: userId,
        ...m,
      })
    }, 350)
    return () => window.clearTimeout(t)
  }, [showNewPeriodForm, userId, daySchedules])

  useEffect(() => {
    if (!showNewPeriodForm || !userId) return
    if (startDate.length !== 10 || endDate.length !== 10) return
    if (!isBulkDateRangeValid(startDate, endDate)) return
    const key = `${startDate}|${endDate}`
    if (key === lastDateRangeKeyRef.current) return
    lastDateRangeKeyRef.current = key
    const pm = computePatternMetrics(daySchedules)
    smartAgendaAnalytics.trackBulkDateRangeSelected({
      professional_id: userId,
      ...pm,
      date_range_weeks: computeDateRangeWeeks(startDate, endDate),
    })
  }, [showNewPeriodForm, userId, startDate, endDate, daySchedules])

  useEffect(() => {
    if (!showNewPeriodForm || !userId) return
    if (!isBulkDateRangeValid(startDate, endDate)) return
    const m = computePatternMetrics(daySchedules)
    if (m.patterns_count === 0) return
    if (bulkPreviewSentRef.current) return
    bulkPreviewSentRef.current = true
    smartAgendaAnalytics.trackBulkPreviewViewed({
      professional_id: userId,
      ...m,
      date_range_weeks: computeDateRangeWeeks(startDate, endDate),
    })
  }, [showNewPeriodForm, userId, startDate, endDate, daySchedules])

  const goBack = () => {
    history.push('/configurar-agenda')
  }

  const handleStartDateChange = (value: string) => {
    const masked = applyDateMask(value)
    setStartDate(masked)
    setDateError('')

    if (masked.length === 10 && endDate.length === 10) {
      validateDates(masked, endDate)
    }
  }

  const handleEndDateChange = (value: string) => {
    const masked = applyDateMask(value)
    setEndDate(masked)
    setDateError('')

    if (masked.length === 10 && startDate.length === 10) {
      validateDates(startDate, masked)
    }
  }

  const validateDates = (start: string, end: string): boolean => {
    if (!isValidDate(start)) {
      setDateError('Data de início inválida')
      return false
    }

    if (!isValidDate(end)) {
      setDateError('Data de fim inválida')
      return false
    }

    const startDateParsed = parseDate(start)
    const endDateParsed = parseDate(end)

    if (!startDateParsed || !endDateParsed) {
      setDateError('Datas inválidas')
      return false
    }

    if (endDateParsed < startDateParsed) {
      setDateError('A data de fim não pode ser anterior à data de início')
      return false
    }

    const maxEndDate = addMonths(startDateParsed, 3)
    if (endDateParsed > maxEndDate) {
      setDateError(
        'A data de fim não pode ser mais de 3 meses após a data de início'
      )
      return false
    }

    setDateError('')
    return true
  }

  const validateScheduleTimes = (): boolean => {
    for (const { key } of DAYS_OF_WEEK) {
      const slots = daySchedules[key] || []
      for (const slot of slots) {
        if (!isMeaningfulTimeSlot(slot)) continue

        if (isTimeBelowMinimum(slot.from)) {
          setScheduleError('O horário de início deve ser a partir das 08:00')
          return false
        }
      }
    }

    setScheduleError('')
    return true
  }

  const addTimeSlot = (day: string) => {
    const newId = Date.now().toString()
    setDaySchedules((prev) => ({
      ...prev,
      [day]: [...prev[day], { id: newId, from: '00:00', to: '00:00' }],
    }))
  }

  const removeTimeSlot = (day: string, slotId: string) => {
    setDaySchedules((prev) => ({
      ...prev,
      [day]: prev[day].filter((slot) => slot.id !== slotId),
    }))
  }

  const updateTimeSlot = (
    day: string,
    slotId: string,
    field: 'from' | 'to',
    value: string
  ) => {
    setScheduleError('')
    setDaySchedules((prev) => ({
      ...prev,
      [day]: prev[day].map((slot) =>
        slot.id === slotId ? { ...slot, [field]: value } : slot
      ),
    }))
  }

  const addException = () => {
    const newId = Date.now().toString()
    setExceptions((prev) => [
      ...prev,
      {
        id: newId,
        reason: '',
        startDate: '',
        startTime: '00:00',
        endTime: '00:00',
      },
    ])
  }

  const removeException = (exceptionId: string) => {
    setExceptions((prev) => prev.filter((ex) => ex.id !== exceptionId))
  }

  const updateException = (
    exceptionId: string,
    field: 'reason' | 'startDate' | 'startTime' | 'endTime',
    value: string
  ) => {
    setExceptions((prev) =>
      prev.map((ex) => {
        if (ex.id !== exceptionId) return ex

        if (field === 'startDate') {
          const masked = applyDateMask(value)
          return { ...ex, startDate: masked }
        }

        return { ...ex, [field]: value }
      })
    )
  }

  const handleSubmit = async () => {
    if (!startDate || !endDate) {
      setDateError('Por favor, preencha as datas de início e fim')
      return
    }

    if (!validateDates(startDate, endDate)) {
      return
    }

    if (!selectedSpecialty) {
      setDateError('Por favor, selecione uma especialidade')
      return
    }

    if (!validateScheduleTimes()) {
      return
    }

    const payload = buildWeeklySchedulePayload(
      startDate,
      endDate,
      daySchedules,
      exceptions,
      {
        id: selectedSpecialty.id,
        name: selectedSpecialty.name,
        attendance_time: selectedSpecialty.attendance_time || 30,
      },
      userId,
      countries,
      ['Plantão']
    )

    const patternCtx = computePatternMetrics(daySchedules)
    const bulkCtx = {
      professional_id: userId,
      ...patternCtx,
      date_range_weeks: computeDateRangeWeeks(startDate, endDate),
    }

    smartAgendaAnalytics.trackBulkConfirmed(bulkCtx)

    const result = await createWeeklySchedule(payload, setLoading)

    if (result.success) {
      smartAgendaAnalytics.trackBulkSuccess({
        ...bulkCtx,
        agendas_created_count: result.agendasCreatedCount ?? 0,
        has_conflict: false,
      })
      setShowNewPeriodForm(false)

      loadSavedSchedules(userId)

      setStartDate('')
      setEndDate('')
      setDaySchedules({
        monday: [{ id: '1', from: '00:00', to: '00:00' }],
        tuesday: [{ id: '1', from: '00:00', to: '00:00' }],
        wednesday: [{ id: '1', from: '00:00', to: '00:00' }],
        thursday: [{ id: '1', from: '00:00', to: '00:00' }],
        friday: [{ id: '1', from: '00:00', to: '00:00' }],
        saturday: [{ id: '1', from: '00:00', to: '00:00' }],
        sunday: [],
      })
      setExceptions([])
    } else {
      smartAgendaAnalytics.trackBulkFailed({
        ...bulkCtx,
        has_conflict: Boolean(result.hasConflict),
      })
    }
  }

  const applyRecommendedTemplate = () => {
    setShowNewPeriodForm(true)

    setDaySchedules({
      monday: [
        { id: '1', from: '08:00', to: '12:00' },
        { id: '2', from: '14:00', to: '18:00' },
      ],
      tuesday: [
        { id: '1', from: '08:00', to: '12:00' },
        { id: '2', from: '14:00', to: '18:00' },
      ],
      wednesday: [
        { id: '1', from: '08:00', to: '12:00' },
        { id: '2', from: '14:00', to: '18:00' },
      ],
      thursday: [
        { id: '1', from: '08:00', to: '12:00' },
        { id: '2', from: '14:00', to: '18:00' },
      ],
      friday: [
        { id: '1', from: '08:00', to: '12:00' },
        { id: '2', from: '14:00', to: '18:00' },
      ],
      saturday: [{ id: '1', from: '08:00', to: '12:00' }],
      sunday: [],
    })

    setTimeout(() => {
      window.scrollTo({ top: 600, behavior: 'smooth' })
    }, 100)
  }

  return (
    <Global.Container>
      {loading && <Loading title="Carregando..." />}
      <Header />
      <S.PageContainer data-testid="open-schedule-page">
        <S.PageInner>
          <S.TitleRow>
            <button
              type="button"
              className="back-button"
              data-testid="open-schedule-back"
              onClick={goBack}
            >
              <ArrowBack style={{ fontSize: 20 }} />
            </button>
            <h1 className="page-title" data-testid="open-schedule-title">
              Abertura de horários
            </h1>
          </S.TitleRow>

          <Card styles={S.mainCardSurfaceStyle}>
            <S.PageContent>
              {savedSchedules.length > 0 && (
                <S.SavedSchedulesSection data-testid="open-schedule-saved-list">
                  <div className="saved-section-header">
                    <p className="saved-section-description">
                      Cadastre, visualize e edite os períodos em que você estará
                      disponível para agendamentos de consultas
                    </p>
                    <div className="header-buttons">
                      <button
                        type="button"
                        className="new-period-button"
                        data-testid="open-schedule-header-new-period"
                        onClick={() => {
                          setShowNewPeriodForm(true)
                          setTimeout(() => {
                            window.scrollTo({ top: 600, behavior: 'smooth' })
                          }, 100)
                        }}
                      >
                        Cadastrar novo período
                      </button>
                      <button
                        type="button"
                        className="template-button-header"
                        data-testid="open-schedule-header-template"
                        onClick={applyRecommendedTemplate}
                      >
                        Cadastrar modelo recomendado
                        <AutoAwesome className="template-icon" />
                      </button>
                    </div>
                  </div>

                  <h4
                    className="saved-title"
                    data-testid="open-schedule-saved-heading"
                  >
                    Período cadastrado:
                  </h4>

                  {savedSchedules.map((schedule) => (
                    <div
                      className="saved-schedule-card"
                      key={schedule._id}
                      data-testid={`open-schedule-saved-card-${schedule._id}`}
                    >
                      <div className="card-header">
                        <p className="period-dates">
                          Período ({formatDateFromAPI(schedule.start_date)} até{' '}
                          {formatDateFromAPI(schedule.end_date)}) -{' '}
                          {getSpecialtyName(schedule.specialty_id)}
                        </p>
                        <div className="card-actions">
                          <button
                            type="button"
                            className="action-button delete"
                            data-testid={`open-schedule-delete-schedule-${schedule._id}`}
                            onClick={() => openDeleteModal(schedule)}
                            title="Excluir período"
                          >
                            <img src={DeleteIcon} alt="Excluir" />
                          </button>
                          <button
                            type="button"
                            className="action-button copy"
                            data-testid={`open-schedule-copy-schedule-${schedule._id}`}
                            onClick={() => handleCopySchedule(schedule)}
                            title="Copiar para novo período"
                          >
                            <img src={CopiarIcon} alt="Copiar" />
                          </button>
                        </div>
                      </div>

                      <div className="week-grid">
                        <div className="day-column">
                          <span className="day-name">Segunda-feira</span>
                          <span className="day-hours">
                            {formatDayIntervals(schedule.weekly_intervals, 1)}
                          </span>
                        </div>
                        <div className="day-column">
                          <span className="day-name">Terça-feira</span>
                          <span className="day-hours">
                            {formatDayIntervals(schedule.weekly_intervals, 2)}
                          </span>
                        </div>
                        <div className="day-column">
                          <span className="day-name">Quarta-feira</span>
                          <span className="day-hours">
                            {formatDayIntervals(schedule.weekly_intervals, 3)}
                          </span>
                        </div>
                        <div className="day-column">
                          <span className="day-name">Quinta-feira</span>
                          <span className="day-hours">
                            {formatDayIntervals(schedule.weekly_intervals, 4)}
                          </span>
                        </div>
                        <div className="day-column">
                          <span className="day-name">Sexta-feira</span>
                          <span className="day-hours">
                            {formatDayIntervals(schedule.weekly_intervals, 5)}
                          </span>
                        </div>
                        <div className="day-column">
                          <span className="day-name">Sábado</span>
                          <span className="day-hours">
                            {formatDayIntervals(schedule.weekly_intervals, 6)}
                          </span>
                        </div>
                        <div className="day-column">
                          <span className="day-name">Domingo</span>
                          <span className="day-hours">
                            {formatDayIntervals(schedule.weekly_intervals, 7)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </S.SavedSchedulesSection>
              )}

              {showNewPeriodForm && (
                <>
                  <S.DateRangeSection data-testid="open-schedule-date-range">
                    <div className="date-range-header">
                      <div>
                        <p className="section-title">
                          Selecione a data de início e o fim:
                        </p>
                        <p className="section-subtitle">
                          Poderá definir sua disponibilidade até no máximo 3
                          meses
                        </p>
                      </div>
                      <button
                        type="button"
                        className="template-button"
                        data-testid="open-schedule-form-template"
                        onClick={applyRecommendedTemplate}
                      >
                        Usar modelo recomendado
                        <AutoAwesome className="template-icon" />
                      </button>
                    </div>

                    <div className="date-inputs">
                      {specialties.length > 1 && (
                        <div className="input-group">
                          <label>Área de atuação</label>
                          <select
                            data-testid="open-schedule-specialty"
                            value={selectedSpecialty?.id || ''}
                            onChange={(e) => {
                              const specialty = specialties.find(
                                (s) => s.id === e.target.value
                              )
                              if (specialty) setSelectedSpecialty(specialty)
                            }}
                          >
                            {specialties.map((specialty) => (
                              <option key={specialty.id} value={specialty.id}>
                                {specialty.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div className="input-group">
                        <label>Data de início</label>
                        <div className="input-wrapper">
                          <CalendarToday style={{ fontSize: 16 }} />
                          <input
                            type="text"
                            placeholder="DD/MM/AAAA"
                            data-testid="open-schedule-start-date"
                            value={startDate}
                            onChange={(e) =>
                              handleStartDateChange(e.target.value)
                            }
                            maxLength={10}
                          />
                        </div>
                      </div>
                      <div className="input-group">
                        <label>Data de fim</label>
                        <div className="input-wrapper">
                          <CalendarToday style={{ fontSize: 16 }} />
                          <input
                            type="text"
                            placeholder="DD/MM/AAAA"
                            data-testid="open-schedule-end-date"
                            value={endDate}
                            onChange={(e) =>
                              handleEndDateChange(e.target.value)
                            }
                            maxLength={10}
                          />
                        </div>
                      </div>
                    </div>
                    {dateError && (
                      <p
                        className="date-error"
                        data-testid="open-schedule-date-error"
                      >
                        {dateError}
                      </p>
                    )}
                  </S.DateRangeSection>

                  {scheduleError && (
                    <p
                      className="date-error"
                      data-testid="open-schedule-schedule-error"
                      style={{ color: 'red' }}
                    >
                      {scheduleError}
                    </p>
                  )}

                  <S.WeekScheduleGrid data-testid="open-schedule-week-grid">
                    {DAYS_OF_WEEK.map((day) => (
                      <S.DayCard
                        key={day.key}
                        data-testid={`open-schedule-day-${day.key}`}
                        isEmpty={daySchedules[day.key].length === 0}
                      >
                        <div className="day-header">
                          <span className="day-name">{day.label}</span>
                          <button
                            type="button"
                            className="add-time-button"
                            data-testid={`open-schedule-add-slot-${day.key}`}
                            onClick={() => addTimeSlot(day.key)}
                          >
                            Adicionar horário <Add style={{ fontSize: 14 }} />
                          </button>
                        </div>

                        {daySchedules[day.key].length === 0 ? (
                          <p className="no-schedule-text">
                            Você não possui horários configurados
                          </p>
                        ) : (
                          daySchedules[day.key].map((slot) => (
                            <div
                              className="time-slot"
                              key={slot.id}
                              data-testid={`open-schedule-slot-${day.key}-${slot.id}`}
                            >
                              <div className="time-input-group">
                                <label>De</label>
                                <div className="time-input-wrapper">
                                  <AccessTime style={{ fontSize: 14 }} />
                                  <input
                                    type="time"
                                    min={MIN_SCHEDULE_TIME}
                                    data-testid={`open-schedule-time-from-${day.key}-${slot.id}`}
                                    value={slot.from}
                                    onChange={(e) =>
                                      updateTimeSlot(
                                        day.key,
                                        slot.id,
                                        'from',
                                        e.target.value
                                      )
                                    }
                                  />
                                </div>
                              </div>
                              <div className="time-input-group">
                                <label>Até</label>
                                <div className="time-input-wrapper">
                                  <AccessTime style={{ fontSize: 14 }} />
                                  <input
                                    type="time"
                                    data-testid={`open-schedule-time-to-${day.key}-${slot.id}`}
                                    value={slot.to}
                                    onChange={(e) =>
                                      updateTimeSlot(
                                        day.key,
                                        slot.id,
                                        'to',
                                        e.target.value
                                      )
                                    }
                                  />
                                </div>
                              </div>
                              <button
                                type="button"
                                className="remove-slot-button"
                                data-testid={`open-schedule-remove-slot-${day.key}-${slot.id}`}
                                onClick={() => removeTimeSlot(day.key, slot.id)}
                              >
                                <Delete style={{ fontSize: 16 }} />
                              </button>
                            </div>
                          ))
                        )}
                      </S.DayCard>
                    ))}
                  </S.WeekScheduleGrid>

                  <S.ExceptionsSection data-testid="open-schedule-exceptions">
                    <div className="exceptions-header">
                      <div>
                        <h3>Exceções:</h3>
                        <p>
                          Selecione o período de exceções para bloquear
                          automaticamente a sua agenda
                        </p>
                      </div>
                      <button
                        type="button"
                        className="add-exception-button"
                        data-testid="open-schedule-add-exception"
                        onClick={addException}
                      >
                        Adicionar ausência <Add style={{ fontSize: 14 }} />
                      </button>
                    </div>

                    {exceptions.length === 0 ? (
                      <p
                        className="no-exceptions-text"
                        data-testid="open-schedule-exceptions-empty"
                      >
                        Não há exceções
                      </p>
                    ) : (
                      <div className="exceptions-list">
                        {exceptions.map((exception) => (
                          <div
                            className="exception-item-wrapper"
                            key={exception.id}
                            data-testid={`open-schedule-exception-${exception.id}`}
                          >
                            <div className="exception-reason-row">
                              <div className="exception-input-group full-width">
                                <label>Escreva o motivo</label>
                                <input
                                  type="text"
                                  placeholder="Escreva o motivo da ausência"
                                  data-testid={`open-schedule-exception-reason-${exception.id}`}
                                  value={exception.reason}
                                  onChange={(e) =>
                                    updateException(
                                      exception.id,
                                      'reason',
                                      e.target.value
                                    )
                                  }
                                  className="reason-input"
                                />
                              </div>
                            </div>
                            <div className="exception-fields-row">
                              <div className="exception-input-group">
                                <label>Data de início</label>
                                <div className="exception-input-wrapper">
                                  <CalendarToday style={{ fontSize: 14 }} />
                                  <input
                                    type="text"
                                    placeholder="DD/MM/AAAA"
                                    data-testid={`open-schedule-exception-start-date-${exception.id}`}
                                    value={exception.startDate}
                                    onChange={(e) =>
                                      updateException(
                                        exception.id,
                                        'startDate',
                                        e.target.value
                                      )
                                    }
                                    maxLength={10}
                                  />
                                </div>
                              </div>
                              <div className="exception-input-group">
                                <label>Hora inicial</label>
                                <div className="exception-input-wrapper">
                                  <AccessTime style={{ fontSize: 14 }} />
                                  <input
                                    type="time"
                                    data-testid={`open-schedule-exception-start-time-${exception.id}`}
                                    value={exception.startTime}
                                    onChange={(e) =>
                                      updateException(
                                        exception.id,
                                        'startTime',
                                        e.target.value
                                      )
                                    }
                                  />
                                </div>
                              </div>
                              <div className="exception-input-group">
                                <label>Hora final</label>
                                <div className="exception-input-wrapper">
                                  <AccessTime style={{ fontSize: 14 }} />
                                  <input
                                    type="time"
                                    data-testid={`open-schedule-exception-end-time-${exception.id}`}
                                    value={exception.endTime}
                                    onChange={(e) =>
                                      updateException(
                                        exception.id,
                                        'endTime',
                                        e.target.value
                                      )
                                    }
                                  />
                                </div>
                              </div>
                              <button
                                type="button"
                                className="remove-exception-button"
                                data-testid={`open-schedule-remove-exception-${exception.id}`}
                                onClick={() => removeException(exception.id)}
                              >
                                <Delete style={{ fontSize: 16 }} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </S.ExceptionsSection>

                  <S.PageFooter data-testid="open-schedule-form-footer">
                    <button
                      type="button"
                      className="cancel-button"
                      data-testid="open-schedule-form-cancel"
                      onClick={() => {
                        if (savedSchedules.length > 0) {
                          setShowNewPeriodForm(false)
                        } else {
                          goBack()
                        }
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="submit-button"
                      data-testid="open-schedule-form-submit"
                      onClick={handleSubmit}
                    >
                      Cadastrar novo período
                    </button>
                  </S.PageFooter>
                </>
              )}
            </S.PageContent>
          </Card>
        </S.PageInner>
      </S.PageContainer>
      <Footer />

      {deleteModal.isOpen && (
        <S.DeleteModalOverlay data-testid="open-schedule-delete-modal-overlay">
          <S.DeleteModal data-testid="open-schedule-delete-modal">
            <div className="modal-title-block">
              <h3 data-testid="open-schedule-delete-modal-title">
                Excluir período cadastrado
              </h3>
              <p>
                Apenas os horários livres serão excluídos, os horários já
                agendados não serão afetados. Você realmente deseja excluir o
                período de {deleteModal.startDate} a {deleteModal.endDate}?
              </p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="cancel-button"
                data-testid="open-schedule-delete-modal-cancel"
                onClick={closeDeleteModal}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="delete-button"
                data-testid="open-schedule-delete-modal-confirm"
                onClick={confirmDeleteSchedule}
              >
                Excluir
              </button>
            </div>
          </S.DeleteModal>
        </S.DeleteModalOverlay>
      )}
    </Global.Container>
  )
}

export default OpenSchedule
